import {
  createFuncaoSalario,
  deleteFuncaoSalario,
  listFuncaoSalarios,
  ReadFuncaoSalarioDto,
  updateFuncaoSalario,
} from '@/modules/structure/funcao/funcao.service';
import { formatarMoeda } from '@/shared/utils/numeros';
import { extrairYyyyMm, formatarYyyyMmMesExtensoOuIndefinido, mesAnoParaPrimeiroDia } from '@/shared/utils/datas';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useState } from 'react';

interface Props {
  funcaoId: number;
}

export function FuncaoSalariosSection({ funcaoId }: Props) {
  const [salarios, setSalarios] = useState<ReadFuncaoSalarioDto[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [vigencia, setVigencia] = useState('');
  const [valor, setValor] = useState('');
  const [editandoId, setEditandoId] = useState<number | null>(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    listFuncaoSalarios(funcaoId)
      .then(setSalarios)
      .catch((err) => toast.error(String(err)))
      .finally(() => setCarregando(false));
  }, [funcaoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const limparFormulario = () => {
    setVigencia('');
    setValor('');
    setEditandoId(null);
  };

  const handleSalvar = async () => {
    if (!vigencia || valor === '') {
      toast.error('Informe competência e valor.');
      return;
    }

    const valorNum = Number(valor);
    if (Number.isNaN(valorNum) || valorNum < 0) {
      toast.error('Valor inválido.');
      return;
    }

    try {
      if (editandoId) {
        await updateFuncaoSalario(editandoId, {
          vigencia: mesAnoParaPrimeiroDia(vigencia),
          valor: valorNum,
        });
        toast.success('Salário atualizado.');
      } else {
        await createFuncaoSalario(funcaoId, {
          vigencia: mesAnoParaPrimeiroDia(vigencia),
          valor: valorNum,
        });
        toast.success('Salário cadastrado.');
      }
      limparFormulario();
      carregar();
    } catch (err) {
      toast.error(String(err));
    }
  };

  const handleEditar = (item: ReadFuncaoSalarioDto) => {
    setEditandoId(item.id);
    setVigencia(extrairYyyyMm(item.vigencia));
    setValor(String(item.valor));
  };

  const handleExcluir = async (id: number) => {
    if (!window.confirm('Remover este registro salarial?')) return;
    try {
      await deleteFuncaoSalario(id);
      toast.warning('Salário removido.');
      if (editandoId === id) limparFormulario();
      carregar();
    } catch (err) {
      toast.error(String(err));
    }
  };

  return (
    <div className="mt-4">
      <h5>Histórico salarial</h5>
      <p className="text-muted small">
        Cadastre o valor da função por competência (mês/ano). O simulador usa o salário vigente na data informada.
      </p>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-3">
          <label htmlFor="salario_vigencia" className="form-label">Competência</label>
          <input
            id="salario_vigencia"
            type="month"
            className="form-control"
            value={vigencia}
            onChange={(e) => setVigencia(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label htmlFor="salario_valor" className="form-label">Valor (R$)</label>
          <input
            id="salario_valor"
            type="number"
            min="0"
            step="0.01"
            className="form-control"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <button type="button" className="btn btn-primary me-2" onClick={() => void handleSalvar()}>
            {editandoId ? 'Atualizar' : 'Adicionar'}
          </button>
          {editandoId && (
            <button type="button" className="btn btn-outline-secondary" onClick={limparFormulario}>
              Cancelar
            </button>
          )}
        </div>
      </div>

      {carregando ? (
        <p className="text-muted">Carregando salários...</p>
      ) : (
        <table className="table table-sm table-bordered table-striped">
          <thead>
            <tr>
              <th>Competência</th>
              <th className="text-end">Valor</th>
              <th className="text-center" style={{ width: 120 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {salarios.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-muted text-center">Nenhum salário cadastrado.</td>
              </tr>
            ) : (
              salarios.map((item) => (
                <tr key={item.id}>
                  <td>{formatarYyyyMmMesExtensoOuIndefinido(extrairYyyyMm(item.vigencia))}</td>
                  <td className="text-end">{formatarMoeda(item.valor)}</td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary me-1"
                      onClick={() => handleEditar(item)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => void handleExcluir(item.id)}
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
